import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Palette, GripVertical, LayoutDashboard, Save, RotateCcw, KeyRound, Eye, EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePreferences } from '../context/PreferencesContext';
import { hardRefresh } from '../components/CacheBuster';
import { CompanySettingsCard } from '../components/CompanySettingsCard';
import { BorrowerProfileCard } from '../components/BorrowerProfileCard';
import { ConflictResolverCard } from '../components/ConflictResolverCard';
import { WebhookCard } from '../components/WebhookCard';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const ACCENT_COLORS = [
  { value: '#10B981', label: 'Emeraude', class: 'bg-emerald-500' },
  { value: '#3B82F6', label: 'Bleu', class: 'bg-blue-500' },
  { value: '#8B5CF6', label: 'Violet', class: 'bg-violet-500' },
  { value: '#F59E0B', label: 'Ambre', class: 'bg-amber-500' },
  { value: '#EF4444', label: 'Rouge', class: 'bg-red-500' },
  { value: '#EC4899', label: 'Rose', class: 'bg-pink-500' },
  { value: '#06B6D4', label: 'Cyan', class: 'bg-cyan-500' },
  { value: '#F97316', label: 'Orange', class: 'bg-orange-500' },
];

const NAV_LABELS = {
  '/app': 'Tableau de bord',
  '/app/recherche': 'Recherche Biens',
  '/app/etude': 'Etude Rentabilite',
  '/app/visites': 'Visites Techniques',
  '/app/offres': "Offres d'achat",
  '/app/pipeline': 'Pipeline Projets',
  '/app/travaux': 'Suivi Travaux',
  '/app/bordereau': 'Bordereau de prix',
  '/app/bons-commande': 'Bons de commande',
  '/app/dossier-financement': 'Dossier de financement',
  '/app/calendrier': 'Calendrier',
  '/app/reseau': 'Réseau',
  '/app/documents': 'Documents',
};

const PasswordSection = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async () => {
    if (next.length < 8) { toast.error('8 caractères minimum'); return; }
    if (next !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true });
      toast.success('Mot de passe modifié');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="password-section">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <KeyRound className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
          Modifier le mot de passe
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-zinc-400 text-xs">Mot de passe actuel</Label>
            <Input type={show ? 'text' : 'password'} value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="current-password" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Nouveau mot de passe</Label>
            <Input type={show ? 'text' : 'password'} value={next}
              onChange={(e) => setNext(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="new-password" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Confirmer</Label>
            <Input type={show ? 'text' : 'password'} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="confirm-password" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <button onClick={() => setShow(s => !s)} className="text-xs text-zinc-400 flex items-center gap-1">
            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {show ? 'Masquer' : 'Afficher'}
          </button>
          <Button onClick={handleChange} disabled={saving || !current || !next}
            className="text-white"
            style={{ backgroundColor: 'var(--accent-color, #10B981)' }}
            data-testid="change-password-btn">
            {saving ? 'Modification...' : 'Modifier le mot de passe'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const SettingsPage = () => {
  const { prefs, setPrefs, savePrefs } = usePreferences();
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePrefs(prefs);
      toast.success('Préférences enregistrées');
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setPrefs({
      ...prefs,
      theme: { accent_color: '#10B981', sidebar_position: 'left' },
      sidebar_order: Object.keys(NAV_LABELS),
      dashboard_widgets: [
        { id: 'stats', visible: true, label: 'Statistiques' },
        { id: 'pipeline_chart', visible: true, label: 'Pipeline' },
        { id: 'pie_chart', visible: true, label: 'Repartition' },
        { id: 'recent_projects', visible: true, label: 'Projets recents' },
      ],
    });
  };

  const setAccentColor = (color) => {
    setPrefs(prev => ({ ...prev, theme: { ...prev.theme, accent_color: color } }));
  };

  const toggleWidget = (widgetId) => {
    setPrefs(prev => ({
      ...prev,
      dashboard_widgets: prev.dashboard_widgets.map(w =>
        w.id === widgetId ? { ...w, visible: !w.visible } : w
      )
    }));
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setPrefs(prev => {
      const order = [...prev.sidebar_order];
      const [moved] = order.splice(dragIdx, 1);
      order.splice(idx, 0, moved);
      setDragIdx(idx);
      return { ...prev, sidebar_order: order };
    });
  };
  const handleDragEnd = () => setDragIdx(null);

  const sidebarOrder = useMemo(() => {
    const saved = prefs.sidebar_order || [];
    const allPaths = Object.keys(NAV_LABELS);
    // Ajoute toujours les éventuels chemins manquants (ex: nouveaux modules ajoutés ultérieurement)
    const missing = allPaths.filter((p) => !saved.includes(p));
    return saved.length > 0 ? [...saved, ...missing] : allPaths;
  }, [prefs.sidebar_order]);

  // Persist missing paths into prefs.sidebar_order so drag-and-drop works with the full list
  useEffect(() => {
    const saved = prefs.sidebar_order || [];
    const allPaths = Object.keys(NAV_LABELS);
    const missing = allPaths.filter((p) => !saved.includes(p));
    if (missing.length > 0 && saved.length > 0) {
      setPrefs((prev) => ({ ...prev, sidebar_order: [...saved, ...missing] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const widgets = prefs.dashboard_widgets || [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Personnalisation</h1>
          <p className="text-zinc-400 mt-1">Adaptez l'interface à vos préférences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser
          </Button>
          <Button className="text-white" style={{ backgroundColor: 'var(--accent-color, #10B981)' }}
            onClick={handleSave} disabled={saving} data-testid="save-settings-button">
            <Save className="w-4 h-4 mr-2" /> Enregistrer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Frais de structure société (large card on top) */}
        <CompanySettingsCard />

        {/* Profil emprunteur — alimente le dossier de financement */}
        <BorrowerProfileCard />

        {/* Conflits de synchronisation hors-ligne */}
        <ConflictResolverCard />

        {/* Webhook alertes immobilières (Make.com) */}
        <WebhookCard />

        {/* Theme */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Palette className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
              Couleur d'accent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3">
              {ACCENT_COLORS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setAccentColor(c.value)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                    prefs.theme?.accent_color === c.value
                      ? 'border-white bg-zinc-800'
                      : 'border-zinc-800 hover:border-zinc-600'
                  }`}
                  data-testid={`accent-${c.value.slice(1)}`}
                >
                  <div className={`w-8 h-8 rounded-full ${c.class}`} />
                  <span className="text-xs text-zinc-400">{c.label}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 p-3 rounded-lg"
              style={{ backgroundColor: (prefs.theme?.accent_color || '#10B981') + '15',
                       borderColor: (prefs.theme?.accent_color || '#10B981') + '30', borderWidth: 1 }}>
              <p className="text-sm" style={{ color: prefs.theme?.accent_color || '#10B981' }}>
                Aperçu de la couleur d'accent sélectionnée
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Dashboard Widgets */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
              Widgets du tableau de bord
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {widgets.map(w => (
              <div key={w.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-sm text-white">{w.label}</span>
                <Switch checked={w.visible} onCheckedChange={() => toggleWidget(w.id)} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Sidebar Order */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <GripVertical className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
              Ordre de la sidebar (glissez-déposez)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {sidebarOrder.map((path, idx) => {
                const label = NAV_LABELS[path];
                if (!label) return null;
                return (
                  <div
                    key={path}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-grab active:cursor-grabbing transition-all ${
                      dragIdx === idx ? 'bg-zinc-800' : 'border-zinc-800 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                    style={dragIdx === idx ? { borderColor: 'var(--accent-color, #10B981)' } : undefined}
                    data-testid={`sidebar-order-${path.replace(/\//g,'_')}`}
                  >
                    <GripVertical className="w-4 h-4 text-zinc-600" />
                    <span className="text-sm text-zinc-300 font-mono">{idx + 1}.</span>
                    <span className="text-sm text-white">{label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Password */}
        <PasswordSection />

        {/* Cache / Mise à jour */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="cache-section">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
              Cache de l'application
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-zinc-400">
              Si certains correctifs ne s'affichent pas (surtout sur mobile), videz le cache puis rechargez.
              L'application détecte aussi automatiquement les nouvelles versions déployées et vous propose un rafraîchissement.
            </p>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white"
              onClick={hardRefresh}
              data-testid="clear-cache-button"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Vider le cache et recharger
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
